import type { MouseEventHandler } from 'react';
import styled from 'styled-components';
import {
  getColorWithAlpha,
  getHandleActionKeyDown,
  getInvertedColor
} from '@elfsight/widget-utils';
import { useSettings } from '@wu/widget-core';
import { getAnimation } from '../../../helpers';
import { Icon as IconComponent, useTheme } from '../../common';

export interface ButtonData {
  label: string;
  icon: string;
  actionUrl?: string;
}

interface RssButtonProps {
  data: ButtonData;
}

export function RssButton({
  data: { actionUrl: link, icon, label }
}: RssButtonProps) {
  const { accentColor } = useSettings();
  const { textColor } = useTheme();

  const handleClick: MouseEventHandler = (event) => event.stopPropagation();

  return (
    <StyledButtonComponent
      href={link}
      role="button"
      target="_blank"
      title={label}
      onClick={handleClick}
      onKeyDown={getHandleActionKeyDown((event) => {
        (event.target as HTMLDivElement).click();
      })}
      _buttonColor={accentColor}
    >
      <IconContainer>
        <Icon name={icon} _fill={textColor} />
      </IconContainer>
    </StyledButtonComponent>
  );
}

export const StyledButtonComponent = styled.a<{
  _buttonColor: string;
}>`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  flex-wrap: wrap;
  height: 20px;
  width: 20px;
  flex: 0 0 20px;
  border-radius: 4px;
  position: relative;
  overflow: hidden;
  background-color: transparent;
  cursor: pointer;
  opacity: 0.7;
  transition: ${getAnimation('opacity')};

  &:before {
    content: '';
    position: absolute;
    z-index: 2;
    width: 100%;
    height: 100%;
    left: 0;
    top: 0;
    background-color: ${({ _buttonColor }) =>
      getColorWithAlpha(getInvertedColor(_buttonColor), 0.1)};
    opacity: 0;
    transition: ${getAnimation('scale')}, ${getAnimation('opacity')};
    border-radius: 50%;
    transform: scale(0.1);
  }

  &:hover:before {
    opacity: 1;
    transform: scale(1.5);
  }

  &:hover {
    opacity: 1 !important;
  }
`;

const IconContainer = styled.div`
  z-index: 3;
  display: flex;
`;

const Icon = styled(IconComponent)<{ _fill: string }>`
  display: flex;
  z-index: 3;
  width: 100%;
  height: 100%;
  fill: ${({ _fill }) => _fill};
`;
